<script>
export default {
	name: 'SsoCrossDomain',
	inheritAttrs: false,
};
</script>

<script setup>
import { Teleport } from 'vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { getCookie, setCookie, deleteCookie } from '~/logic/helpers/cookies.js';
import { useEventBus } from '@vueuse/core';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry';

const props = defineProps({
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	yesBtnLabel: { type: String, default: 'Yes' },
	noBtnLabel: { type: String, default: 'No' },
	iframeSrc: { type: String, default: '' },
	stateParams: { type: String, default: '' },
	iframeTitle: { type: String, default: 'sandbox-login' },
	sureText: { type: String, default: 'Are You Sure' },
	confirmationText: { type: String, default: 'Please Confirm' },
	timerText: { type: String, default: '60' },
	logoutUrl: { type: String, default: '' },
	logoutStateParams: { type: String, default: '' },
	postLogoutUrl: { type: String, default: '' },
});

const emit = defineEmits(['session-event']);
const { isEditorMode } = useGlobalAEMState();
const { deleteLocalStorage } = usePersistencyWithExpiry('userInfo', {});

const iframeContainer = ref(null);
const countdown = ref(0);
const intervalId = ref(null);
const userName = ref(null);
const isPopUpVisible = ref(false);
const hasHandledSSO = ref(false);
const siteName = window.siteName;

const SSO_CROSS_DOMAIN = 'ssoCrossDomain';
const ID_TOKEN = 'id_token';
const POLICY_CODE = 'policyCode';
const SAPPHIRE = 'sapphire';

const createIframeIfNotExists = () => {
	if (!props.iframeSrc || !props.stateParams) {
		emit('session-event', { type: 'noLoginRequired', isFallbackTriggered: false });
		return;
	}
	if (iframeContainer.value?.querySelector(`iframe[title="${props.iframeTitle}"]`)) return;
	const iframe = document.createElement('iframe');
	iframe.src = props.iframeSrc.replace('{state}', props.stateParams);
	iframe.title = props.iframeTitle;
	iframe.className = 'hidden';
	iframeContainer.value?.appendChild(iframe);
};

const handlePostSsoLogin = (event) => {
	if (event.origin !== window.location.origin) return;
	if (hasHandledSSO.value) return;
	if (event.data?.type !== 'SSO_CALLBACK_PARAMS') return;
	hasHandledSSO.value = true;
	clearTimeout(iframeTimeout.value);
	if ((!event && !event?.data) || event.data?.data?.error) {
		emit('session-event', { type: 'noLoginRequired', isFallbackTriggered: false });
		return true;
	}
	userName.value = `${event.data?.data?.firstName} ${event.data?.data?.lastName}`;
	isPopUpVisible.value = true;
	const d = new Date();
	d.setTime(d.getTime() + (30 * 60 * 1000));
	setCookie(SSO_CROSS_DOMAIN, 'true', d.toUTCString());
	setCookie(SAPPHIRE, event.data?.data?.access_token, d.toUTCString());
	setCookie(POLICY_CODE, event.data?.data?.policy_code, d.toUTCString());
	setCookie(ID_TOKEN, event.data?.data?.id_token, d.toUTCString());
	countdown.value = parseInt(props.timerText, 10);
	intervalId.value = setInterval(() => {
		if (countdown.value > 0) {
			countdown.value--;
		} else {
			clearInterval(intervalId.value);
			emit('session-event', { type: 'continue' });
			isPopUpVisible.value = false;
		}
	}, 1000);
};

const callSandBoxApiAndLoadIframe = () => {
	createIframeIfNotExists();
	window.addEventListener('message', handlePostSsoLogin);
};

const deleteCrossDomainCookies = () => {
	deleteCookie(POLICY_CODE);
	deleteCookie(ID_TOKEN);
	deleteCookie(SSO_CROSS_DOMAIN);
};

const notifyUserSession = (type) => {
	if (type === 'continue') {
		emit('session-event', { type: 'continue' });
	}

	if (type === 'abort') {
		deleteCookie(SAPPHIRE);
		deleteLocalStorage('userInfo');
		emit('session-event', { type: 'abort' });
		clearSsoSessionOnLogout();
	}
	isPopUpVisible.value = false;
};

const sureTextWithQuestion = computed(() => {
	return props.sureText?.trim().endsWith('?')
		? props.sureText
		: `${props.sureText}?`;
});

const crossDomainBus = useEventBus('clearCrossDomainCookies');
crossDomainBus.on((event, payload) => {
	if (event === 'user-logout-action-sso') {
		clearSsoSessionOnLogout(payload);
	}
});
const clearSsoSessionOnLogout = (data) => {
	const isComponentVisible = isMobileViewport.value
		? !props.isHideInMobile
		: !props.isHideInDesktop;
	if (isComponentVisible) {
		const idToken = getCookie(ID_TOKEN);
		const hasAllRequiredProps =
			props.logoutUrl &&
			props.logoutStateParams &&
			props.postLogoutUrl;
		deleteCrossDomainCookies();
		if (idToken && hasAllRequiredProps) {
			window.location.href = `${props.logoutUrl}?id_token_hint=${idToken}&state=${props.logoutStateParams}&post_logout_redirect_uri=${props.postLogoutUrl}`;
		}
	} else if (siteName == 'enrich-portal') {
		window.location.href = data?.logoutUrl;
	}

};
const iframeTimeout = ref(null);
onMounted(() => {
	
	const isComponentVisible = isMobileViewport.value
		? !props.isHideInMobile
		: !props.isHideInDesktop;

	if (isComponentVisible && !getCookie(SSO_CROSS_DOMAIN) && !getCookie(SAPPHIRE)) {
		callSandBoxApiAndLoadIframe();
		iframeTimeout.value = setTimeout(() => {
			console.warn('[SSO] iframe did not respond in time — possible mixed content or JS failure.');
			emit('session-event', { type: 'noLoginRequired', isFallbackTriggered: true });
		}, 8000); // adjust delay as needed
	} else {
		emit('session-event', { type: 'noLoginRequired', isFallbackTriggered: false });
	}
});

onBeforeUnmount(() => {
	clearInterval(intervalId.value);
	clearTimeout(iframeTimeout);
	window.removeEventListener('message', handlePostSsoLogin);
});

defineExpose({
	clearSsoSessionOnLogout,
});

</script>

<template>
<div ref="iframeContainer" class="iframe-body"></div>
<component :is="isEditorMode ? 'div' : Teleport" to="body">
	<Transition name="overlay-fade-and-zoom" appear>
		<div v-if="isPopUpVisible" class="SsoCrossDomain backdrop">
			<div class="overlay-container">

				<div class="overlay-body px-6 mt-4">
					<div v-html-sanitize="sureTextWithQuestion" class="author-html-container line-clamp-5"></div>
					<div>{{ userName }} ?</div>
					<div v-html-sanitize="props.confirmationText" class="mt-2"></div>
				</div>

				<div class="overlay-footer px-6 mt-6 mb-10">
					<div class="flex-grow basis-0">
						<CTAButton
							:ctaText="props.noBtnLabel"
							sizeType="small"
							ctaSizing="fluid"
							styleType="secondary"
							containerClass="flex-grow basis-0"
							@click="notifyUserSession('abort')"
						/>
					</div>

					<div class="flex-grow basis-0">
						<CTAButton
							:ctaText="`${props.yesBtnLabel} (${countdown})`"
							sizeType="small"
							ctaSizing="fluid"
							styleType="primary"
							containerClass="w-full"
							tabindex="-1"
							@click="notifyUserSession('continue')"
						/>
					</div>
				</div>
			</div>
		</div>
	</Transition>
</component>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SsoCrossDomain {
	&.backdrop {
		position: fixed;
		z-index: 10000;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: color.change(black, $alpha: 0.8);
		display: flex;
		justify-content: flex-end;
		transition: opacity 0.45s ease;
	}

	.overlay-container {
		background-color: var(--overlay-container-bg-color, white);
		max-width: 408px;
		width: 100%;
		margin: auto;
		overflow: hidden;
		@apply rounded-3xl;
	}

	.overlay-header {
		position: relative;
	}

	.overlay-footer {
		display: flex;
		align-items: center;
		justify-content: center;
		@apply gap-5;

		@media #{var.$query-max-md} {
			flex-direction: column;
			@apply gap-4;
		}
	}
}

</style>
